import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { Doc } from "building-types/doc";
import { docEntityTypes } from "building-types/doc/docEntityTypes";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { OptionSetHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityHandle, CatalogItemHandle } from "building-types/handles/docEntityHandleTypes";
import { SettingsType } from "building-types/settings/settingsType";
import { optionIdNew, optionSetIdNew } from "doc/options/optionSetTools";
import { isSystemContentTemplateId } from "../contentTemplateTools";
import { docGetCatalogItem } from "../docGetCatalogItem";
import { builtInPropAttributes } from "../elements/propCatalog/builtInProps";
import { docEntityTypeToDocTableName } from "../entity/docEntityTypeToDocTableName";
import { isSystemParameterId } from "../systemParameterTools";

export function docGetEntityHandleById(
  doc: Doc,
  id: string,
  entityTypes: DocEntityType[] = docEntityTypes,
): DocEntityHandle | null {
  // Treat empty array as "search all types"
  const effectiveEntityTypes = entityTypes.length > 0 ? entityTypes : docEntityTypes;

  for (const entityType of effectiveEntityTypes) {
    switch (entityType) {
      case "CATALOG_ITEM": {
        const catalogItemHandle: CatalogItemHandle = { type: "CATALOG_ITEM", itemId: id };
        if (docGetCatalogItem(doc, catalogItemHandle)) {
          return catalogItemHandle;
        }
        break;
      }
      case "PROP": {
        const propAttributes = doc.propAttributes?.[id] || builtInPropAttributes[id];
        if (propAttributes) {
          return { type: "PROP", id };
        }
        break;
      }
      case "OPTION_SET": {
        const optionSetId = optionSetIdNew(id);

        if (doc.optionSets?.[optionSetId]) {
          return { type: "OPTION_SET", id: optionSetId };
        }
        break;
      }
      case "OPTION": {
        const optionId = optionIdNew(id);

        let optionSetHandle: OptionSetHandle | null = null;

        for (const optionSetId in doc.optionSets) {
          const optionSet = doc.optionSets[optionSetId];
          if (optionSet.options?.[optionId]) {
            optionSetHandle = { type: "OPTION_SET", id: optionSet.id };
            break;
          }
        }

        if (optionSetHandle) {
          return { type: "OPTION", optionKey: optionId, optionSetId: optionSetHandle.id };
        }

        break;
      }
      case "ELECTRICAL_RULE": {
        for (const settingsId in doc.settings) {
          const settings = doc.settings[settingsId];

          if (settings.type !== SettingsType.ELECTRICAL_SETTINGS) {
            continue;
          }

          if (settings.rules?.[id]) {
            return { type: "ELECTRICAL_RULE", id, settingsHandle: { type: "SETTINGS", id: settingsId } };
          }
        }

        break;
      }
      case "SYSTEM_PARAMETER": {
        if (isSystemParameterId(doc, id)) {
          return { type: "SYSTEM_PARAMETER", id };
        }
        break;
      }
      case "DATA_SEQUENCE": {
        if (isSystemDataSequenceId(id) || doc.dataSequences[id]) {
          return { type: "DATA_SEQUENCE", id };
        }
        break;
      }
      case "CONTENT_TEMPLATE": {
        if (isSystemContentTemplateId(id) || doc.contentTemplates[id]) {
          return { type: "CONTENT_TEMPLATE", id };
        }
        break;
      }
      case "SHAPE":
      case "EDGE":
      case "VERTEX":
      case "ROOF_SIDE":
      case "EDIT_MODE":
      case "GENERATED_ENTITY":
      case "AUTO_DIMENSION":
        continue;
      default: {
        const docField = docEntityTypeToDocTableName[entityType];

        if (!docField) {
          continue;
        }

        if (doc[docField]?.[id]) {
          return { type: entityType, id } as DocEntityHandle;
        }
      }
    }
  }

  return null;
}
